import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { TriaLogo } from "./Mark";
import { EASE } from "../../config";

export const LINKS = [
  ["/quem-somos", "about"],
  ["/o-que-fazemos", "what"],
  ["/metodo-construtor", "method"],
  ["/contacto", "contact"],
];

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-full border border-[#121518]/20 p-0.5 text-[11px] font-semibold tracking-[0.15em]" data-testid="lang-toggle">
      {["pt", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-btn`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`rounded-full px-3 py-1.5 uppercase transition-colors duration-300 ${lang === l ? "bg-[#121518] text-[#FAF8F5]" : "text-[#121518] hover:bg-[#121518]/5"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Nav = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${scrolled || open ? "border-b border-[#E2DDD5] bg-[#FAF8F5]/90 backdrop-blur-xl" : "border-b border-transparent"}`}>
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-6 sm:px-10">
        <Link to="/" data-testid="nav-logo" aria-label="TRIA — início"><TriaLogo /></Link>
        <nav className="hidden items-center gap-9 lg:flex">
          {LINKS.map(([to, key]) => (
            <NavLink key={to} to={to} data-testid={`nav-${key}`} className={({ isActive }) => `link-u text-[13px] tracking-wide ${isActive ? "text-[#005F60]" : "text-[#121518]"}`}>
              {t.nav[key]}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle />
          <span className="hidden md:block"><Link to="/contacto" data-testid="nav-cta-btn" className="btn-dark">{t.nav.cta}</Link></span>
          <button className="grid h-10 w-10 place-items-center lg:hidden" onClick={() => setOpen((o) => !o)} aria-label={open ? t.nav.close : t.nav.menu} data-testid="nav-menu-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            data-lenis-prevent
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="h-[calc(100dvh-72px)] bg-[#FAF8F5] px-6 pt-8 lg:hidden"
          >
            {[["/", "home"], ...LINKS].map(([to, key], i) => (
              <Link key={to} to={to} data-testid={`mobile-nav-${key}`} className="flex items-baseline gap-4 border-b border-[#E2DDD5] py-5">
                <span className="text-xs text-[#4A525A]">0{i + 1}</span>
                <span className="f-serif text-3xl">{t.nav[key]}</span>
              </Link>
            ))}
            <Link to="/contacto" className="btn-dark mt-8 inline-flex" data-testid="mobile-nav-cta">{t.nav.cta}</Link>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
